import { api } from "../api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PatronLookupResult {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phone: string | null;
  patronType: { id: string; name: string; borrowLimit: number } | null;
  cardNumber: string | null;
  cardStatus: string | null;
}

export interface CheckoutRecord {
  id: string;
  tenantId: string;
  itemId: string | null;
  bookId: string | null;
  userId: string;
  checkoutBranchId: string | null;
  returnBranchId: string | null;
  checkoutDate: string;
  dueDate: string;
  returnDate: string | null;
  status: "ACTIVE" | "RETURNED" | "OVERDUE" | "LOST";
  renewalCount: number;
  notes: string | null;
  item?: {
    id: string;
    barcode: string;
    accessionNumber: number;
    biblio: { id: string; title: string; authors: string[]; coverUrl?: string };
    itemType?: { id: string; name: string; code: string } | null;
    branch?: { id: string; name: string; code: string } | null;
  };
  book?: { id: string; title: string; author: string } | null;
  user: { id: string; firstName: string | null; lastName: string | null; email: string };
  checkoutBranch?: { id: string; name: string; code: string } | null;
  returnBranch?: { id: string; name: string; code: string } | null;
  Renewals?: { id: string; previousDueDate: string; newDueDate: string; renewedAt: string }[];
  Fines?: FineRecord[];
}

export interface HoldRecord {
  id: string;
  tenantId: string;
  biblioId: string | null;
  itemId: string | null;
  userId: string;
  pickupBranchId: string | null;
  status: "pending" | "ready" | "fulfilled" | "cancelled" | "expired";
  priority: number;
  reservedAt: string;
  readyAt: string | null;
  fulfilledAt: string | null;
  expiresAt: string | null;
  notes: string | null;
  item?: {
    id: string;
    barcode: string;
    biblio: { title: string; authors: string[] };
    branch?: { name: string } | null;
  };
  biblio?: { id: string; title: string; authors: string[] } | null;
  user: { id: string; firstName: string | null; lastName: string | null; email: string };
  pickupBranch?: { id: string; name: string; code: string } | null;
}

export interface FineRecord {
  id: string;
  checkoutId: string | null;
  userId: string;
  amount: string;
  reason: string;
  status: "PENDING" | "PAID" | "WAIVED" | "CANCELLED";
  notes: string | null;
  paidAt: string | null;
  waivedAt: string | null;
  createdAt: string;
  user?: { id: string; firstName: string | null; lastName: string | null; email: string };
  checkout?: {
    id: string;
    dueDate: string;
    item?: { biblio: { title: string } };
  } | null;
}

export interface PatronSummary {
  patron: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
    phone: string | null;
    patronType: {
      id: string;
      name: string;
      borrowLimit: number;
      renewalLimit: number;
      borrowingPeriodDays: number;
    } | null;
    homeBranchId: string | null;
  } | null;
  checkouts: CheckoutRecord[];
  holds: HoldRecord[];
  fines: FineRecord[];
  totalUnpaidFines: number;
  blocks: { blocked: boolean; reasons: string[] };
  stats: {
    activeCheckouts: number;
    pendingHolds: number;
    readyHolds: number;
    overdueItems: number;
  };
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface BatchResult {
  results: any[];
  errors: { barcode?: string; accessionNumber?: number; error: string }[];
  totalSuccess: number;
  totalErrors: number;
}

export interface CheckInResult {
  checkout: CheckoutRecord;
  fineCreated: FineRecord | null;
  holdFulfilled: HoldRecord | null;
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

export const circulationApi = {
  // Patron lookup
  lookupPatron: (query: string) =>
    api
      .get<PatronLookupResult[]>("circulation/patron/lookup", { params: { q: query } })
      .then((r) => r.data),

  getPatronSummary: (userId: string) =>
    api.get<PatronSummary>(`circulation/patron/${userId}/summary`).then((r) => r.data),

  // Checkout
  checkout: (data: {
    barcode?: string;
    accessionNumber?: number;
    itemId?: string;
    userId: string;
    branchId?: string;
    dueDate?: string;
    inHouse?: boolean;
    override?: { reason: string };
  }) => api.post<CheckoutRecord>("circulation/checkout", data).then((r) => r.data),

  // Check-in
  checkIn: (data: {
    barcode?: string;
    accessionNumber?: number;
    checkoutId?: string;
    branchId?: string;
    returnDate?: string;
  }) => api.post<CheckInResult>("circulation/check-in", data).then((r) => r.data),

  // Renew
  renew: (checkoutId: string, newDueDate?: string) =>
    api.put<CheckoutRecord>(`circulation/renew/${checkoutId}`, { newDueDate }).then((r) => r.data),

  // Batch
  batchCheckout: (data: {
    barcodes?: string[];
    accessionNumbers?: number[];
    userId: string;
    branchId?: string;
    override?: { reason: string };
  }) => api.post<BatchResult>("circulation/batch/checkout", data).then((r) => r.data),

  batchCheckIn: (data: {
    barcodes?: string[];
    accessionNumbers?: number[];
    branchId?: string;
    returnDate?: string;
  }) => api.post<BatchResult>("circulation/batch/check-in", data).then((r) => r.data),

  // Checkouts list
  getCheckouts: (filters?: {
    userId?: string;
    status?: string;
    branchId?: string;
    page?: number;
    limit?: number;
  }) =>
    api
      .get<PaginatedResponse<CheckoutRecord>>("circulation/checkouts", { params: filters })
      .then((r) => r.data),

  // Overdue
  getOverdue: (filters?: { branchId?: string; page?: number; limit?: number }) =>
    api
      .get<PaginatedResponse<CheckoutRecord>>("circulation/overdue", { params: filters })
      .then((r) => r.data),

  // Holds
  placeHold: (data: {
    biblioId?: string;
    itemId?: string;
    userId?: string;
    pickupBranchId?: string;
    notes?: string;
  }) => api.post<HoldRecord>("circulation/holds", data).then((r) => r.data),

  getHolds: (filters?: {
    userId?: string;
    status?: string;
    branchId?: string;
    page?: number;
    limit?: number;
  }) =>
    api
      .get<PaginatedResponse<HoldRecord>>("circulation/holds", { params: filters })
      .then((r) => r.data),

  fulfillHold: (holdId: string) =>
    api.put<HoldRecord>(`circulation/holds/${holdId}/fulfill`).then((r) => r.data),

  cancelHold: (holdId: string) =>
    api.delete<HoldRecord>(`circulation/holds/${holdId}`).then((r) => r.data),

  // Fines
  getFines: (filters?: { userId?: string; status?: string; page?: number; limit?: number }) =>
    api
      .get<PaginatedResponse<FineRecord>>("circulation/fines", { params: filters })
      .then((r) => r.data),

  waiveFine: (fineId: string, notes?: string) =>
    api.put<FineRecord>(`circulation/fines/${fineId}/waive`, { notes }).then((r) => r.data),

  // Lost / Damaged
  declareLost: (checkoutId: string, data: { replacementCost?: number; notes?: string }) =>
    api.put(`circulation/checkouts/${checkoutId}/declare-lost`, data).then((r) => r.data),

  declareDamaged: (
    checkoutId: string,
    data: { fineAmount: number; damageDescription: string; notes?: string }
  ) => api.put(`circulation/checkouts/${checkoutId}/declare-damaged`, data).then((r) => r.data),

  // Overdue export
  exportOverdue: (filters?: { branchId?: string }) =>
    api.get("circulation/overdue/export", { params: filters }).then((r) => r.data),

  // Circulation Policies
  getPolicies: (filters?: { patronTypeId?: string; itemTypeId?: string; branchId?: string }) =>
    api.get("circulation/policies", { params: filters }).then((r) => r.data),

  upsertPolicy: (data: {
    patronTypeId: string;
    itemTypeId: string;
    branchId?: string;
    loanPeriodDays: number;
    renewalLimit: number;
    fineRatePerDay: number;
    gracePeriodDays: number;
    maxFineAmount?: number;
    isActive?: boolean;
  }) => api.post("circulation/policies", data).then((r) => r.data),

  deletePolicy: (policyId: string) =>
    api.delete(`circulation/policies/${policyId}`).then((r) => r.data),
};
